import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { useDebounceFn } from '@vueuse/core'
import { groupBy } from 'lodash-es'
import { decompressJson } from '@/utils/Utils'
import { logger } from '@/utils/logger'

export const getTypes = async ({ boardId }) => {
  try {
    const url = new URL(`${await API_URL()}/custom-fields/${boardId}`)

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else if (ret.status === 404) return []
    else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

export const fetchValues = async ({ boardId }) => {
  const ret = await axios.get(`${await API_URL()}/custom-fields/${boardId}/values`, {
    headers: { ...(await getAuthHeader()) }
  })

  if (ret.status === 200) {
    return decompressJson(ret.data) || []
  } else throw new Error(ret.statusText)
}

// Initialize pendingRequests as a Map to track ongoing requests
const pendingRequests = new Map()

const batchedRequests = new Map()

const CLEANUP_INTERVAL = 60000 // 1 minute interval for cleanup

const getRequestKey = (boardId, personId) => `${boardId}:${personId}`

const fetchValuesForPersonsDebounced = useDebounceFn(async () => {
  const queuedRequests = Array.from(batchedRequests.values())
  batchedRequests.clear()

  if (queuedRequests.length === 0) return

  const requestsByBoard = groupBy(queuedRequests, 'boardId')

  await Promise.all(
    Object.entries(requestsByBoard).map(async ([boardId, boardRequests]) => {
      const personIds = [...new Set(boardRequests.map(({ personId }) => personId))]

      if (personIds.length === 0) return

      try {
        const ret = await axios.post(
          `${await API_URL()}/custom-fields/${boardId}/values-batch`,
          { data: { personIds } },
          { headers: { ...(await getAuthHeader()) } }
        )

        if (ret.status !== 200) {
          throw new Error(ret.statusText)
        }
        const data = decompressJson(ret.data) || []
        const result = groupBy(data, 'personId') || {}

        personIds.forEach((personId) => {
          const requestKey = getRequestKey(boardId, personId)
          const pendingRequest = pendingRequests.get(requestKey)

          if (!pendingRequest) return

          pendingRequest.resolve(result[personId] || [])
          pendingRequests.delete(requestKey)
        })
      } catch (error) {
        logger.error('Error fetching batched custom field values:', error.message)

        personIds.forEach((personId) => {
          const requestKey = getRequestKey(boardId, personId)
          const pendingRequest = pendingRequests.get(requestKey)

          if (!pendingRequest) return

          pendingRequest.reject(error)
          pendingRequests.delete(requestKey)
        })
      }
    })
  )
}, 500)

async function addToQueue({ boardId, personId }) {
  const requestKey = getRequestKey(boardId, personId)

  if (pendingRequests.has(requestKey)) {
    // If a request is already pending for this board/person pair, return the existing promise
    return pendingRequests.get(requestKey).promise
  }

  // Create a new promise for this request
  let resolveFn, rejectFn
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve
    rejectFn = reject
  })

  // Add to pendingRequests
  pendingRequests.set(requestKey, { promise, resolve: resolveFn, reject: rejectFn })

  batchedRequests.set(requestKey, { boardId, personId })

  // Fetch using the debounced function
  fetchValuesForPersonsDebounced()

  try {
    // Await the promise associated with this personId
    const result = await promise
    return result
  } catch (error) {
    logger.error('Error in addToQueue:', error.message)
    return { error: error.message }
  }
}

export const fetchValuesForPersonBatched = async ({ boardId, personId }) => {
  if (!boardId || !personId) return []

  if (personId.includes('-collapsed-')) return []

  try {
    const result = await addToQueue({ boardId, personId })

    if (result && result.error) {
      throw new Error(result.error)
    }

    return result || []
  } catch (error) {
    logger.error('Error in fetchValuesForPersonBatched:', error.message)
    return { error: error.message }
  }
}

export const filter = async ({ boardId, personIds, fields }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}/filter`,
      { data: { personIds, fields } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return decompressJson(ret.data) || []
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

export const getUniqueValues = async ({ boardId }) => {
  try {
    const url = new URL(`${await API_URL()}/custom-fields/${boardId}/unique-values`)

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return decompressJson(ret.data) || []
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

export const addOrUpdateField = async ({ boardId, fields }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}`,
      { data: { fields } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else {
      throw new Error(`Unexpected status code: ${ret.status} - ${ret.statusText}`)
    }
  } catch (error) {
    logger.error('API Service Error in addOrUpdateField')
    throw error
  }
}

export const bulkUpdate = async ({ boardId, peopleIds, values, historyMeta = null }) => {
  const ret = await axios.post(
    `${await API_URL()}/custom-fields/${boardId}/bulk-values`,
    { data: { values, peopleIds, ...(historyMeta ? { _historyMeta: historyMeta } : {}) } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export const addOrUpdateValues = async ({ boardId, fields, historyMeta = null }) => {
  try {
    const data = { fields, ...(historyMeta ? { _historyMeta: historyMeta } : {}) }
    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}/values`,
      {
        data
      },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('API Service Error in addOrUpdateValues', error.message)
    throw error
  }
}

export const remove = async ({ boardId, fieldId }) => {
  try {
    const ret = await axios.delete(`${await API_URL()}/custom-fields/${boardId}/${fieldId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

// Cleanup function to remove old data and prevent memory leaks
function cleanup() {
  // No need to clean batchedRequests as it's being reset after each batch
  // However, ensure pendingRequests that are no longer needed are cleaned up
  // Optionally, implement logic to remove old entries based on timestamps if necessary
}

setInterval(cleanup, CLEANUP_INTERVAL)

// Get people grouped by custom field values (for kanban board)
export const getPeopleByField = async ({ boardId, fieldId, filters = [], sortField = null }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}/people-by-field/${fieldId}`,
      {
        data: {
          filters,
          sortField
        }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return decompressJson(ret.data) || {}
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error fetching people by field:', error.message)
    return {}
  }
}

// Get custom fields (alias for getTypes)
export const getCustomFields = getTypes

// Update a person's custom field value
export const updatePersonCustomField = async ({ boardId, personId, fieldId, value }) => {
  try {
    const fields = [
      {
        personId,
        fieldId,
        data: value
      }
    ]

    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}/values`,
      {
        data: { fields }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error updating person custom field:', error.message)
    throw error
  }
}

// Fetch custom field values in batch (alias for existing function pattern)
export const fetchCustomFieldValuesBatch = async ({ boardId, personIds, fieldIds }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/custom-fields/${boardId}/values-batch`,
      {
        data: { personIds, fieldIds }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return decompressJson(ret.data) || {}
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error fetching custom field values batch:', error.message)
    return {}
  }
}
